import { EventStatus } from "@garther/interfaces";
import { Button } from "@garther/ui/components";
import { cn, useI18n } from "@garther/ui/libs";
import { createFileRoute, useNavigate, useRouteContext } from "@tanstack/react-router";
import { AlignJustify, LayoutGrid, Plus, Search, SlidersHorizontal } from "lucide-react";
import { useState } from "react";

import { useEventHostStats } from "@/features/event/hooks/use-events-query";

import { EventsTable } from "../-components/events-table";
import { Stats } from "./-components/stats";

type ViewMode = "list" | "grid";

export const Route = createFileRoute("/_auth/org/$orgId/event/")({
  component: RouteComponent
});

function RouteComponent() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { org } = useRouteContext({ from: "/_auth/org/$orgId/event/" });

  const [statusFilter, setStatusFilter] = useState<EventStatus | null>(null);
  const [search, setSearch] = useState("");
  const [viewMode, setViewMode] = useState<ViewMode>("list");

  const { data: hostStats } = useEventHostStats(org.id);

  const statusTabs = [
    {
      value: null,
      label: t("org.events.filter.all"),
      count: hostStats?.totalEvents
    },
    {
      value: EventStatus.AVAILABLE,
      label: t("event.status.available"),
      count: hostStats?.byStatus.AVAILABLE
    },
    {
      value: EventStatus.LOCKED,
      label: t("event.status.locked"),
      count: hostStats?.byStatus.LOCKED
    },
    {
      value: EventStatus.FINISHED,
      label: t("event.status.finished"),
      count: hostStats?.byStatus.FINISHED
    }
  ];

  const handleCreateEvent = () => {
    navigate({ to: "/org/$orgId/event/create", params: { orgId: org.id } });
  };

  return (
    <div className="py-4 md:py-6 space-y-4">
      <div className="flex sm:flex-row flex-col items-start sm:items-center gap-4 justify-between">
        <div>
          <p className="text-responsive-xl font-light text-text-primary uppercase font-literata mb-1">
            {t("org.tabs.events")}
          </p>
          <p className="text-responsive-base font-normal text-text-secondary">
            Quản lý lịch sự kiện, vé và doanh thu của tổ chức.
          </p>
        </div>
        <Button onClick={handleCreateEvent} variant="outline" className="font-normal text-responsive-md">
          <Plus className="h-4 w-4 mr-2" />
          {t("org.createEvent")}
        </Button>
      </div>

      <Stats orgId={org.id} />

      {/* Filter bar */}
      <div className="flex items-center gap-2 flex-wrap">
        {/* Status tabs */}
        <div className="flex items-center gap-1">
          {statusTabs.map((tab) => (
            <button
              key={tab.value ?? "all"}
              onClick={() => {
                setStatusFilter(tab.value);
                setSearch("");
              }}
              className={cn(
                "px-3 py-1.5 rounded-full text-responsive-sm font-medium transition-colors",
                statusFilter === tab.value
                  ? "bg-background-dark text-background-secondary"
                  : "bg-background-secondary text-text-secondary hover:bg-background-highlight"
              )}
            >
              {tab.label}
              {tab.count !== undefined && (
                <span className="ml-1 text-text-secondary">{` (${tab.count})`}</span>
              )}
            </button>
          ))}
        </div>

        {/* Right side: search + filter button + toggle */}
        <div className="flex items-center gap-2 ml-auto">
          {/* Search input */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-text-secondary" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t("org.events.filter.searchPlaceholder")}
              className="pl-9 pr-4 py-2 rounded-full border border-border bg-background-secondary text-responsive-sm text-text-primary placeholder:text-text-secondary focus:outline-none focus:ring-1 focus:ring-border-dark w-48"
            />
          </div>
          {/* Filter button */}
          <Button variant="default" padding="px-3 py-1.5" className="rounded-md">
            <SlidersHorizontal className="h-4 w-4 mr-1.5" />
            {t("org.events.filter.advancedFilter")}
          </Button>
          {/* Grid toggle buttons */}
          <div className="flex items-center border border-border rounded-md overflow-hidden">
            <button
              onClick={() => setViewMode("list")}
              className={cn(
                "p-2 transition-colors",
                viewMode === "list"
                  ? "bg-background-dark text-background-secondary"
                  : "hover:bg-background-highlight text-text-secondary"
              )}
            >
              <AlignJustify className="h-4 w-4" />
            </button>
            <button
              onClick={() => setViewMode("grid")}
              className={cn(
                "p-2 border-l border-border transition-colors",
                viewMode === "grid"
                  ? "bg-background-dark text-background-secondary"
                  : "hover:bg-background-highlight text-text-secondary"
              )}
            >
              <LayoutGrid className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      <EventsTable
        hostId={org.id}
        limit={20}
        statusFilter={statusFilter}
        search={search}
        viewMode={viewMode}
      />
    </div>
  );
}
